import { createStore } from "vuex";
import createPersistedState from "vuex-persistedstate";
import {
    getJourneySets,
    getJourneySetsForCount,
    getQuadrants,
    getJourneys,
    getQuadrant,
    getJourneySet,
    getRecentJourneys,
    addRecentJourney, 
    Preferences,
    checkAndRestartIncompleteDownloads
} from "@/storage/DataStorage";
import {Device} from '@capacitor/device';

/**
 * Common data between UI pages
 */
interface State {
    quadrantId: string;
    journeySetId: string;
    journeyId: string;
    headerImage: string;
    headerTitle: string;
    prevPage: string;
    navigationHistory: string[];
    shouldShowCalculatorIcon: boolean;
    shouldPauseAudio: boolean;
    locale: string;
    messages: any;
    shouldExitApp: boolean;
    loadingValue: number;
    loadingSlug: string;
    bundleVersion: string;
    isInvalidUrl: boolean;
    preferences: Preferences;
    failedAttempts: number;
    lockoutDateTime: Date | null;
    needToSetPin: boolean;
    minRequiredLoadData: any;
    isFirstTimeLoadingApp: boolean;
    checkForVersion: boolean;
    platform: string;
    backgroundJourneySets: number;
    latestBackgroundId: string;
    isSecure: boolean;
    allowNotes: boolean;
    downloadDomain: string;
    //Root Language file aka application instance
    variant: string;
    appVersion: number;
    versionUpdated: boolean;
    secureUpdateOverride: boolean;
    isRelease: boolean;
    pageLoading: boolean;
    logLevel: number;
    importingJourneySet: boolean;
    localDebug: boolean;
    skipBundleUpdates: boolean;
    contentVersion: string;
    dbVersion: number | null;
    migrationRestartRequired: boolean;
    /** Bundled asset assets/offlineSecure.json; never persisted */
    offlineOnlySecure: boolean;
    /** Skip one resume navigation to calculator after system file share/save UI; never persisted */
    suppressSafetyCalculatorOnResume: boolean;
}

export const store = createStore({
    plugins: [createPersistedState({
        paths: [
            'failedAttempts', 'lockoutDateTime', 'isFirstTimeLoadingApp', 'isSecure', 'downloadDomain', 'variant','appVersion', 'bundleVersion', 'contentVersion', 'dbVersion', 'migrationRestartRequired'
        ]
    })],
    state(): State {
        return {
            quadrantId: "",
            journeySetId: "",
            journeyId: "",
            headerImage: "",
            headerTitle: "",
            prevPage: "fields",
            navigationHistory: [] as string[],
            shouldShowCalculatorIcon: true,
            shouldPauseAudio: false,
            locale: "",
            messages: {},
            shouldExitApp: true,
            loadingValue: 0,
            loadingSlug: "",
            bundleVersion: "1.7.44",
            isInvalidUrl: false,
            preferences: {} as any,
            failedAttempts: 0,
            lockoutDateTime: null,
            needToSetPin: false,
            minRequiredLoadData: {},
            isFirstTimeLoadingApp: true,
            checkForVersion: false,
            platform: '',
            backgroundJourneySets: 0,
            latestBackgroundId: '',
            isSecure: true,
            allowNotes: true,
            downloadDomain: '',
            //Root Language file aka application instance
            variant: '',
            appVersion: 0,
            versionUpdated: false,
            secureUpdateOverride: false,
            isRelease: true,
            pageLoading: false,
            logLevel: 2,
            importingJourneySet: false,
            localDebug: true,
            skipBundleUpdates: false,
            contentVersion: "",
            dbVersion: null,
            migrationRestartRequired: false,
            offlineOnlySecure: false,
            suppressSafetyCalculatorOnResume: false
        };
    },

    actions: {
        setQuadrantId({ commit }, quadrantId: string) {
            commit("setQuadrantId", quadrantId);
        },
        setJourneySetId({ commit }, journeySetId: string) {
            commit("setJourneySetId", journeySetId);
        },
        setJourneyId({ commit }, journeyId: string) {
            commit("setJourneyId", journeyId);
        },
        setHeaderImage({ commit }, headerImage: string) {
            commit("setHeaderImage", headerImage);
        },
        setHeaderTitle({ commit }, headerTitle: string) {
            commit("setHeaderTitle", headerTitle);
        },
        setPrevPage({ commit }, routeName: string) {
            commit("setPrevPage", routeName);
        },
        setShouldShowCalculatorIcon({ commit }, shouldShow: boolean) {
            commit("setShouldShowCalculatorIcon", shouldShow);
        },
        addRecentJourney({commit}, journeyId: string) {
            commit("addRecentJourney", journeyId);
        },
        setShouldExitApp({ commit }, shouldExitApp: boolean) {
            commit("setShouldExitApp", shouldExitApp);
        },         
        setJourneySetImporting({ commit }, loading: boolean) {
            commit("setJourneySetImporting", loading);
        },
        setSuppressSafetyCalculatorOnResume({ commit }, value: boolean) {
            commit("setSuppressSafetyCalculatorOnResume", value);
        },
        setShouldPauseAudio({ commit }, shouldPauseAudio: boolean) {
            commit("setShouldPauseAudio", shouldPauseAudio);
        },
        setLocale({ commit }, locale: string) {
            commit("setLocale", locale);
        },
        setMessages({ commit }, messages: any) {
            commit("setMessages", messages);
        },
        setMinRequiredLoadData({ commit }, messages: any) {
            commit("setMinRequiredLoadData", messages);
        },
        setLoadingValue({ commit }, payload: { value: number, slug: string }) {
            commit("setLoadingValue", payload);
        },
        setBundleVersion({commit}, version: string) {
            commit("setBundleVersion",version);
        },
        setIsInvalidUrl({ commit }, invalid: boolean) {
            commit("setIsInvalidUrl", invalid);
        },
        setPreferences({ commit }, preferences: Preferences) {
            commit("setPreferences", preferences);
        },
        incrementFailedAttempts({ commit }) {
            commit("incrementFailedAttempts");
        },
        setLockoutDateTime({ commit }) {
            commit("setLockoutDateTime");
        },
        clearLockoutDateTime({ commit }) {
            commit("clearLockoutDateTime");
        },
        clearFailedAttempts({commit}) {
            commit("clearFailedAttempts");
        },
        setNeedToSetPin({ commit }, needToSetPin: boolean) {
            commit("setNeedToSetPin", needToSetPin);
        },
        setIsFirstTimeOpeningApp({ commit }, firstTime: boolean) {
            commit("setIsFirstTimeOpeningApp", firstTime);
        },
        setCheckForVersion({ commit }, checkForVersion: boolean) {
            commit("setCheckForVersion", checkForVersion);
        },
        setPlatform({ commit }) {
            commit("setPlatform");
        },
        incrementBackgroundJourneySets({ commit }, id: any) {
            commit("incrementBackgroundJourneySets", id);
        },
        setAppVersion({commit}, version: number) {
            commit("setAppVersion",version);
        },
        setVersionUpdated({commit}, versionUpdated: boolean){
            commit("setVersionUpdated" , versionUpdated);
        },
        setSecureUpdateOverride({commit}, secureUpdateOverride: boolean){
            commit("setSecureUpdateOverride" , secureUpdateOverride);
        },
        addRouteHistory({ commit }, routeName: string) {
            commit("addRouteHistory", routeName);
        },
        popRouteHistory({ commit }, routeName: string) {
            commit("popRouteHistory", routeName);
        },
        clearRouteHistory({ commit }) {
            commit("clearRouteHistory");
        },
        setSimpleCategoryShapes({ commit }, value: boolean) {
            commit("setSimpleCategoryShapes", value);
        },
        setDownloadDomain({ commit }, value: string) {
            commit("setDownloadDomain", value);
        },
        setVariant({ commit }, value: string) {
            commit("setVariant", value);
        },
        setIsRelease({ commit }, value: boolean) {
            commit("setIsRelease", value);
        },
        setPageLoading({ commit }, value: boolean) {
            commit("setPageLoading", value);
        },
        async checkIncompleteDownloads() {
            await checkAndRestartIncompleteDownloads();
        },
        setSkipBundleUpdates({ commit }, value: boolean) {
            commit("setSkipBundleUpdates", value);
        },
        setContentVersion({ commit }, value: string) {
            commit("setContentVersion", value);
        },
        setDbVersion({ commit }, value: number | null) {
            commit("setDbVersion", value);
        },
        setMigrationRestartRequired({ commit }, value: boolean) {
            commit("setMigrationRestartRequired", value);
        },
        setOfflineOnlySecure({ commit }, value: boolean) {
            commit("setOfflineOnlySecure", value);
        }
    },

    mutations: {
        setQuadrantId(state: any, quadrantId: string) {
            state.quadrantId = quadrantId;
        },
        setJourneySetId(state: any, journeySetId: string) {
            state.journeySetId = journeySetId;
        },
        setJourneyId(state: any, journeyId: string) {
            state.journeyId = journeyId;
        },
        setHeaderImage(state: any, headerImage: string) {
            state.headerImage = headerImage;
        },
        setHeaderTitle(state: any, headerTitle: string) {
            state.headerTitle = headerTitle;
        },
        setPrevPage(state: any, routeName: string) {
            state.prevPage = routeName;
        },
        setShouldShowCalculatorIcon(state: any, shouldShow: boolean) {
           state.shouldShowCalculatorIcon = shouldShow;
        },
        setShouldPauseAudio(state: any, shouldPauseAudio: boolean) {
            state.shouldPauseAudio = shouldPauseAudio;
        },
        setShouldExitApp(state: any, shouldExitApp: boolean) {
            if(state.isSecure){
                state.shouldExitApp = shouldExitApp;
            }else{
                state.shouldExitApp = false;
            }
            
        },
        async addRecentJourney(state: any, journeyId: string) {
            await addRecentJourney(journeyId);
        },
        setLocale(state: any, locale: string) {
            state.locale = locale;
        },
        setMessages(state: any, messages: any) {
            state.messages = messages;
        },
        async addRouteHistory(state: any, routeName: string) {
            if (!routeName.toLowerCase().includes("calculator")){
                state.navigationHistory.push(routeName);
            }  
        },
        async popRouteHistory(state: any, routeName: string) {
            const index = state.navigationHistory.lastIndexOf(routeName);
            if (index > -1) {
                state.navigationHistory.splice(index, 1);
            }
        },
        async clearRouteHistory(state: any) {
            state.navigationHistory = [];
        },
        /**
         * Minimum data for functional application
         */
        setMinRequiredLoadData(state: any, data: {locale: any; quadrants: any;}) {
            state.minRequiredLoadData = data;
            const { locale } = data as any;
            state.locale = Object.keys(locale)[0];
            state.messages = locale[state.locale];
            state.isSecure = state.messages.buildConfigs.config.isSecure === 'true';
            state.logLevel = state.messages.buildConfigs.config.logLevel ? parseInt(state.messages.buildConfigs.config.logLevel) : 2;
            state.simpleCategoryShapes = state.messages.buildConfigs.config.simpleCategoryShapes === 'true';
            state.allowNotes = store.getters.isPwa() ? false : state.messages.buildConfigs.config.allowNotes === 'true';
            state.downloadDomain = state.messages.application.downloadDomain;
            console.log('[setMinRequiredLoadData] language.variant:', state.messages.language?.variant);
            state.variant = state.messages.language.variant;
            state.isRelease = state.messages.language.isRelease === 'true';
        },
        setAppVersion(state: any, version: number) {
            if(version > state.appVersion) {
                state.appVersion = version;
            }
        },
        setLoadingValue(state: any, payload: { value: number, slug: string }) {
            if (payload.value > state.loadingValue) {
                state.loadingValue = payload.value;
                if (state.logLevel > 2) { 
                    state.loadingSlug = payload.slug;
                }else{
                    state.loadingSlug = "";
                }
            }
        },
        setBundleVersion(state: any, version: string) {
            state.bundleVersion = version;
        },
        setIsInvalidUrl(state: any, invalid: boolean) {
            state.isInvalidUrl = invalid;
        },
        setPreferences(state: any, preferences: Preferences) {
            state.preferences = preferences;
        },
        incrementFailedAttempts(state: any) {
            state.failedAttempts = state.failedAttempts === 20 ? state.failedAttempts : state.failedAttempts + 1;
        },
        setLockoutDateTime(state: any) {
            state.lockoutDateTime = new Date();
        },
        clearLockoutDateTime(state: any) {
            state.lockoutDateTime = null;
        },
        clearFailedAttempts(state: any) {
            state.failedAttempts = 0;
        },
        setNeedToSetPin(state: any, needToSetPin: boolean) {
            state.needToSetPin = needToSetPin;
        },
        setIsFirstTimeOpeningApp(state: any, isFirstTime: boolean) {
            state.isFirstTimeLoadingApp = isFirstTime;
        },
        setCheckForVersion(state: any, checkForVersion: boolean) {
            state.checkForVersion = checkForVersion;
        },
        /**
         * Device.getInfo() is an expensive call, stashing the result increases computational performance
         * @param state
         */
        async setPlatform(state: any) {
            const deviceInfo = await Device.getInfo();
            state.platform = deviceInfo.platform;
        },
        /**
         * Used by watchers to refresh the journey set page on success of background processing 
         * @param state current state
         * @param id id that completed processing, only reload if this id is on the current page to prevent flickering 
         * unnecessarily 
         */
        incrementBackgroundJourneySets(state: any, id: string) {
            state.latestBackgroundId = id;
            state.backgroundJourneySets++;
        },
        setVersionUpdated(state: any , value: boolean){
            state.versionUpdated = value;
        },
        setSecureUpdateOverride(state: any , value: boolean){
            state.secureUpdateOverride = value;
        },
        setPageLoading(state: any, value: boolean) {
            state.pageLoading = value;
        },
        setJourneySetImporting(state: any, loading: boolean) {
            state.importingJourneySet = loading;
        },
        setSuppressSafetyCalculatorOnResume(state: any, value: boolean) {
            state.suppressSafetyCalculatorOnResume = value;
        },
        setSimpleCategoryShapes(state: any, value: boolean) {
            state.simpleCategoryShapes = value;
        },
        setDownloadDomain(state: any, value: string) {
            state.downloadDomain = value;
        },
        setVariant(state: any, value: string) {
            state.variant = value;
        },
        setIsRelease(state: any, value: boolean) {
            state.isRelease = value;
        },
        setSkipBundleUpdates(state: any, value: boolean) {
            state.skipBundleUpdates = value;
        },
        setContentVersion(state: any, value: string) {
            state.contentVersion = value;
        },
        setDbVersion(state: any, value: number | null) {
            state.dbVersion = value;
        },
        setMigrationRestartRequired(state: any, value: boolean) {
            state.migrationRestartRequired = value;
        },
        setOfflineOnlySecure(state: any, value: boolean) {
            state.offlineOnlySecure = Boolean(value);
        }
    },

    getters: {
        getQuadrants: () => (subType: string): any => {
            return getQuadrants(subType);
        },
        getQuadrant: () => (quadrantId: string): Promise<any> => {
            return getQuadrant(quadrantId);
        },
        getJourneySets: () => (quadrantIdFilter: string): Promise<any> => {
            return getJourneySets(false, quadrantIdFilter);
        },
        getJourneySetsForCount: () => (quadrantIdFilter: string): Promise<any> => {
            return getJourneySetsForCount(false, quadrantIdFilter);
        },
        getJourneySet: () => (journeySetId: string): Promise<any> => {
            return getJourneySet(journeySetId);
        },
        getJourneys: () => (journeySetIdFilter: string): Promise<any> => {
            return getJourneys(false, journeySetIdFilter);
        },
        getPrevPage: (state: State) => (): any => {
            return state.prevPage;
        },
        getLastVisitedRoute: (state: State) => (): string => {
            if(state.navigationHistory.length > 0){
                return state.navigationHistory[state.navigationHistory.length - 1];
            }else{
                return "/fields";
            }
        },
        getRecentJourneys: () => async (): Promise<any> => {
            return await getRecentJourneys();
        },
        getLocale: (state: State) => (): string => {
            return state.locale;
        },
        getLocaleMessages: (state: State) => (): any => {
            return state.messages;
        },
        getMinRequiredLoadData: (state: State) => (): any => {
            return state.minRequiredLoadData;
        },
        getShouldExitApp: (state: State) => (): boolean => {
            if(state.isSecure){ 
                return state.shouldExitApp;
            }else{
                return false;
            }
        },
        getLoadingValue: (state: State) => (): number => {
            return state.loadingValue;
        },
        getIsInvalidUrl: (state: State) => (): boolean => {
            return state.isInvalidUrl;
        },
        getPreferences: (state: State) => (): Preferences => {
            if (Object.keys(state.preferences).length === 0) {
                return {
                    shouldUseSafetyCalculator: false,
                    shouldDisplayQuickExit: true,
                    shouldDisplayUnencryptedAlert: true,
                    shouldDisplayInternetAlert: true,
                    shouldDisplayAppSharedAlert: true,
                    entrancePin: "1234",
                    lastVisitedRoute: "/fields",
                    deletePin: "6666",
                    canAccessApp: true,
                    shouldSetPin: true,
                    isRelease: false,
                    simpleCategoryShapes: false,
                    logLevel: 2,
                    allowAudioInnotes: true,
                    allowNotes: true
                } as Preferences
            }
            return state.preferences;
        },
        getFailedAttempts: (state: State) => (): number => {
            return state.failedAttempts;
        },
        getLockoutDateTime: (state: State) => (): Date | null => {
            return state.lockoutDateTime ? new Date(state.lockoutDateTime) : null;
        },
        getNeedToSetPin: (state: State) => (): boolean => {
            return state.needToSetPin;
        },
        isFirstTimeOpeningApp: (state: State) => (): boolean => {
            return state.isFirstTimeLoadingApp;
        },
        showAllQuadrants: () => (): boolean => {
            let config: any;
            if (store.getters.isFirstTimeOpeningApp()) {
                const { locale } = store.getters.getMinRequiredLoadData();
                config = locale[Object.keys(locale)[0]].buildConfigs.config;
            } else {
                const messages = store.getters.getLocaleMessages();
                config = messages.buildConfigs.config;
            }
            return config.showAllQuadrants === 'true';
        },
        quadrantsToShow: () => (): string[] => {
            return store.getters.showAllQuadrants() ? ['jf1', 'jf2', 'jf3', 'jf4', 'jf5', 'jf6'] : ['jf1', 'jf2', 'jf3', 'jf4'];
        },
        checkForVersion: (state: State) => (): boolean => {
            return state.checkForVersion;
        },
        platform: (state: State) => (): string => {
            return state.platform;
        },
        isPwa: () => (): boolean => {
            return store.getters.platform() === 'web';
        },
        isOnlineApp: () => (): boolean => {
            return !store.getters.isSecure() && !store.getters.isPwa();
        },
        //Is Secure checks PWA, store.state.isSecure just pulls language file setting. This method should eventually 
        // incorporate store.state.isSecure and remove the language file dependency.
        // When locale/messages not yet loaded (e.g. router guard before main.ts), use state.isSecure to avoid throwing.
        isSecure: (state) => (): boolean => {
            if (store.getters.isPwa()) {
                return false;
            }
            try {
                let config: any;
                if (store.getters.isFirstTimeOpeningApp()) {
                    const loadData = store.getters.getMinRequiredLoadData();
                    const locale = loadData?.locale;
                    if (!locale || typeof locale !== 'object' || Object.keys(locale).length === 0) {
                        return state.isSecure;
                    }
                    config = locale[Object.keys(locale)[0]]?.buildConfigs?.config;
                } else {
                    const messages = store.getters.getLocaleMessages();
                    config = messages?.buildConfigs?.config;
                }
                if (config && typeof config.isSecure !== 'undefined') {
                    return config.isSecure === 'true';
                }
            } catch (_) {
                // locale/messages not ready (e.g. before getMinRequiredDataForAppLoad)
            }
            return state.isSecure;
        },
        getBackgroundProgress: (state: State) => (): number => {
            return state.backgroundJourneySets;
        },
        getAppVersion: (state: State) => (): number => {
            return state.appVersion;
        },
        versionUpdated: (state: State) => (): boolean => {
            return state.versionUpdated;
        },
        secureUpdateOverride: (state: State) => (): boolean => {
            return state.secureUpdateOverride;
        },
        getPageLoading: (state: State) => (): boolean => {
            return state.pageLoading;
        },
        getLoglevel: (state: State) => (): number => {
            return state.preferences.logLevel;
        },
        isImportingJourneySet: (state: State) => (): boolean => {
            return state.importingJourneySet;
        },
        getSuppressSafetyCalculatorOnResume: (state: State) => (): boolean => {
            return state.suppressSafetyCalculatorOnResume;
        },
        shouldSkipBundleUpdates: (state: State) => (): boolean => {
            return state.skipBundleUpdates;
        },
        /** Secure Android SKU with bundled offlineOnly flag — blocks all internet use */
        isOfflineOnlySecureAndroid: (state: State) => (): boolean => {
            if (state.platform !== "android") {
                return false;
            }
            return state.offlineOnlySecure === true && store.getters.isSecure();
        },
        /**
         * Central gate for S3 / update / download paths. Offline-only secure Android always false.
         * Otherwise: PWA and non-secure use network; secure only when secureUpdateOverride.
         */
        allowsInternetAccess: (state: State) => (): boolean => {
            if (store.getters.isOfflineOnlySecureAndroid()) {
                return false;
            }
            if (store.getters.isPwa()) {
                return true;
            }
            if (!store.getters.isSecure()) {
                return true;
            }
            return state.secureUpdateOverride === true;
        },
    },
});
